import type { ImplementedLocale } from '~/lib/i18n/shared'
import {
  calendarDateSchema,
  historicalMatchDetailParamsSchema,
  opaquePublicIdSchema,
  publicCompetitionCodeSchema,
} from '@ballpeek/api/client'
import { z } from 'zod'
import {
  footballCoreBackTargetSchema,
  isSafeFootballCoreBackTarget,
} from '~/lib/navigation/football-core'
import { parsePublicFootballRouteInput } from '~/lib/public-football-read'

const dateSchema = calendarDateSchema
const backSchema = z.string().trim().min(1).max(4096).refine(
  value => isSafeFootballCoreBackTarget(value, 'en') || isSafeFootballCoreBackTarget(value, 'zh-CN'),
  'Expected a relative Football Core path',
)

function validateScope(
  search: { competition?: string, season?: number, from?: string, to?: string },
  context: z.RefinementCtx,
) {
  if ((search.competition === undefined) !== (search.season === undefined)) {
    context.addIssue({
      code: 'custom',
      path: search.competition === undefined ? ['competition'] : ['season'],
      message: 'competition and season must be provided together',
    })
  }
  if (search.from !== undefined && search.to !== undefined && search.from > search.to) {
    context.addIssue({
      code: 'custom',
      path: ['to'],
      message: 'to must be on or after from',
    })
  }
}

export const teamHistoryRouteSearchSchema = z.object({
  competition: publicCompetitionCodeSchema.optional(),
  season: z.coerce.number().int().optional(),
  from: dateSchema.optional(),
  to: dateSchema.optional(),
  page: z.coerce.number().int().min(1).max(1000).optional(),
  back: backSchema.optional(),
}).strict().superRefine(validateScope)

export const teamHistorySearchSchema = teamHistoryRouteSearchSchema.transform(search => ({
  ...search,
  page: search.page ?? 1,
}))

export const historicalMatchSearchSchema = z.object({
  back: backSchema.optional(),
}).strict()

export const headToHeadRouteSearchSchema = z.object({
  teamA: opaquePublicIdSchema,
  teamB: opaquePublicIdSchema,
  competition: publicCompetitionCodeSchema.optional(),
  season: z.coerce.number().int().optional(),
  from: dateSchema.optional(),
  to: dateSchema.optional(),
  page: z.coerce.number().int().min(1).max(1000).optional(),
  back: backSchema.optional(),
}).strict().superRefine((search, context) => {
  validateScope(search, context)
  if (search.teamA === search.teamB) {
    context.addIssue({
      code: 'custom',
      path: ['teamB'],
      message: 'teamB must differ from teamA',
    })
  }
})

export const headToHeadSearchSchema = headToHeadRouteSearchSchema.transform(search => ({
  ...search,
  page: search.page ?? 1,
}))

export type TeamHistorySearchInput = z.output<typeof teamHistoryRouteSearchSchema>
export type TeamHistorySearch = z.output<typeof teamHistorySearchSchema>
export type HistoricalMatchSearch = z.output<typeof historicalMatchSearchSchema>
export type HeadToHeadSearchInput = z.output<typeof headToHeadRouteSearchSchema>
export type HeadToHeadSearch = z.output<typeof headToHeadSearchSchema>

export function validateHistoricalMatchRouteParams(
  params: { historicalMatchId: string } & Record<string, unknown>,
): void {
  parsePublicFootballRouteInput(historicalMatchDetailParamsSchema, {
    historicalMatchId: params.historicalMatchId,
  })
}

function validateBackLocale(back: string | undefined, locale: ImplementedLocale): void {
  if (back !== undefined)
    parsePublicFootballRouteInput(footballCoreBackTargetSchema(locale), back)
}

export function parseTeamHistorySearch(
  search: unknown,
  locale: ImplementedLocale = 'en',
): TeamHistorySearchInput {
  const parsed = parsePublicFootballRouteInput(teamHistoryRouteSearchSchema, search)
  validateBackLocale(parsed.back, locale)
  return parsed
}

export function resolveTeamHistorySearch(search: TeamHistorySearchInput): TeamHistorySearch {
  return teamHistorySearchSchema.parse(search)
}

export function parseHistoricalMatchSearch(
  search: unknown,
  locale: ImplementedLocale = 'en',
): HistoricalMatchSearch {
  const parsed = parsePublicFootballRouteInput(historicalMatchSearchSchema, search)
  validateBackLocale(parsed.back, locale)
  return parsed
}

export function parseHeadToHeadSearch(
  search: unknown,
  locale: ImplementedLocale = 'en',
): HeadToHeadSearchInput {
  const parsed = parsePublicFootballRouteInput(headToHeadRouteSearchSchema, search)
  validateBackLocale(parsed.back, locale)
  return parsed
}

export function resolveHeadToHeadSearch(search: HeadToHeadSearchInput): HeadToHeadSearch {
  return headToHeadSearchSchema.parse(search)
}
